import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
  DocumentGroupRenderHelpers,
  DocumentTypeGroup,
  DocumentTypesAccess,
} from '../../../components/document-type-access';
import { WorkflowScopes } from '../../../constants/workflows';
import { CredentialSectionSpec, isCredentialSectionSpec } from './credentials';
import { DocumentRecordCard } from './DocumentRecordCard';
import type { ProviderDrawerMaps } from './providerDisplay';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY } from './queryKeys';
import { SectionItem, SectionList, Selection } from './SectionList';
import { useCredentialSectionItems } from './useCredentialSectionItems';

export type DocumentSectionSpec = {
  /** DocumentType slug. */
  slug: string;
  /**
   * Section title for `count` documents (singular for exactly one, plural otherwise).
   * Defaults to the DocumentType's own title.
   */
  title?: (count: number) => string;
  /** Test id of the section; defaults to `<testIdPrefix>-<slug>`. */
  testId?: string;
  /**
   * A clinician is expected to have at least one of these. While none is on file the
   * type is flagged in the add bar, and the tab's label carries an attention icon
   * (`useMissingRequiredDocuments`).
   */
  required?: boolean;
};

/**
 * A tab's section: a document type (uploads of that type) or a credential kind's
 * section (structured entries with ONE backing document each). A tab lists them in one
 * order and never has to care which is which.
 */
export type SectionSpec = DocumentSectionSpec | CredentialSectionSpec;

type DocumentSectionsProps = {
  providerId: string;
  /**
   * The sections this tab owns. This list IS the display order — of the add bar's
   * buttons and of the sections — whatever order the API returns types or rows in.
   */
  types: readonly SectionSpec[];
  /** Code-definition maps the credential cards label with (needed with credential sections). */
  maps?: ProviderDrawerMaps;
  /** Prefix of the add bar (`-add-bar`), empty line (`-empty`) and section test ids. */
  testIdPrefix: string;
  /**
   * Who and what these documents are, for the name of a zip of them:
   * "John Joe MD Education Documents" → "John Joe MD Education Documents 20261001-1532.zip"
   * (first name first: `providerFileName`).
   */
  zipSubject: string;
  /** Add bar lead-in when none of the sections has anything. */
  noneOnFileLead?: string;
  /** Add bar lead-in when some sections have something and these do not. */
  someOnFileLead?: string;
  /** Shown instead of the bar when nothing is on file and the viewer may not add anything. */
  emptyText?: React.ReactNode;
  /** Shown while the credential entries load. */
  loadingText?: string;
  /** Rendered above the bar and the sections. */
  header?: React.ReactNode;
  /** Extra queries the host must refetch after a change (the documents count is handled here). */
  onDocumentsChanged?: () => void;
};

/**
 * The standard way a staff clinician drawer tab shows its collections
 * (patterns/add-in-context-document-access.md) — document types and credential entry
 * sections alike, mixed in one order, through one `SectionList`: cards for the sections
 * with something on file, one add bar for the rest, one selection bar for downloads.
 * Document sections ride on `DocumentTypesAccess` (one query per type, the same keys the
 * Documents tab uses); credential sections on `useCredentialSectionItems`.
 */
export function DocumentSections({
  providerId,
  types,
  onDocumentsChanged,
  ...rest
}: DocumentSectionsProps) {
  const queryClient = useQueryClient();
  const documentTypeSlugs = types
    .filter((spec): spec is DocumentSectionSpec => !isCredentialSectionSpec(spec))
    .map((spec) => spec.slug);
  if (documentTypeSlugs.length === 0) {
    // entries only (the Licenses & Certs and Immunizations tabs): no document queries
    return <SectionsBody providerId={providerId} types={types} groups={[]} {...rest} />;
  }
  return (
    <DocumentTypesAccess
      contextType={WorkflowScopes.Provider}
      contextId={providerId}
      ownerId={providerId}
      documentTypeSlugs={documentTypeSlugs}
      onDocumentsChanged={() => {
        queryClient.invalidateQueries({
          queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
        });
        onDocumentsChanged?.();
      }}
    >
      {(groups, helpers) => (
        <SectionsBody
          providerId={providerId}
          types={types}
          groups={groups}
          helpers={helpers}
          {...rest}
        />
      )}
    </DocumentTypesAccess>
  );
}

/** A document type's group as a section item. */
function documentSectionItem(
  spec: DocumentSectionSpec,
  group: DocumentTypeGroup,
  helpers: DocumentGroupRenderHelpers,
  testIdPrefix: string
): SectionItem {
  const { renderLink, renderMenu, renderUpload, canDownload } = helpers;
  const downloadable = group.documents.filter(canDownload);
  return {
    key: group.slug,
    title: spec.title ? spec.title(group.documents.length) : group.title,
    label: group.title,
    count: group.documents.length,
    required: Boolean(spec.required),
    canAdd: group.canUpload,
    renderAdd: (as, content) =>
      renderUpload(group.slug, as === 'link' ? { as: 'link', text: content } : { text: content }),
    selectableIds: downloadable.map((document) => document.id),
    renderCards: (selection: Selection) =>
      group.documents.map((document) => (
        <DocumentRecordCard
          key={document.id}
          document={document}
          renderLink={renderLink}
          renderMenu={renderMenu}
          testIdPrefix={`${testIdPrefix}-document`}
          leading={
            canDownload(document)
              ? selection.renderCheckbox(document.id, document.label || document.original_filename)
              : null
          }
        />
      )),
    testId: spec.testId,
  };
}

function SectionsBody({
  providerId,
  types,
  groups,
  helpers,
  maps,
  testIdPrefix,
  loadingText = 'Loading...',
  emptyText = 'No documents on file.',
  header,
  ...listProps
}: Omit<DocumentSectionsProps, 'onDocumentsChanged'> & {
  groups: DocumentTypeGroup[];
  helpers?: DocumentGroupRenderHelpers;
}) {
  const credentialSections = types.filter(isCredentialSectionSpec);
  const entries = useCredentialSectionItems({
    providerId,
    sections: credentialSections,
    maps,
    testIdPrefix,
  });
  const items = types
    .map((spec): SectionItem | null => {
      if (isCredentialSectionSpec(spec)) {
        return entries.items.find((item) => item.key === spec.key) ?? null;
      }
      const group = groups.find((candidate) => candidate.slug === spec.slug);
      return group && helpers ? documentSectionItem(spec, group, helpers, testIdPrefix) : null;
    })
    .filter((item): item is SectionItem => item !== null);

  // the header stays mounted in the same spot across loading (it may hold its own
  // queries and dialogs — the IMLC line's letter — which a remount would reset)
  return (
    <>
      {header}
      {entries.isLoading ? (
        <span className="tw:text-sm tw:text-zinc-500">{loadingText}</span>
      ) : (
        <SectionList
          items={items}
          providerId={providerId}
          testIdPrefix={testIdPrefix}
          emptyText={emptyText}
          {...listProps}
        >
          {credentialSections.length > 0 && entries.dialogs}
        </SectionList>
      )}
    </>
  );
}
